// Shapes returned by the bpm-lambdas `nexxen` service. Current values come live from Nexxen.

export const NexxenSource = {
  AGENT: 'agent',
  BPM_UI: 'bpm_ui',
  NEXXEN: 'nexxen'
} as const;
export type NexxenSourceType = (typeof NexxenSource)[keyof typeof NexxenSource];

export const SyncStatus = {
  PENDING: 'pending',
  CREATED: 'created',
  FAILED: 'failed',
  ARCHIVED: 'archived'
} as const;
export type SyncStatusType = (typeof SyncStatus)[keyof typeof SyncStatus];

export const NexxenStatus = {
  ACTIVE: 'active',
  PAUSE: 'pause'
} as const;
export type LineItemStatusType = typeof NexxenStatus.ACTIVE | typeof NexxenStatus.PAUSE;

export const PausedReason = {
  FLIGHT_CANCELED: 'flight_canceled'
} as const;
export type PausedReasonType = (typeof PausedReason)[keyof typeof PausedReason];

export interface ObjectSummary {
  name: string | null;
  status: string | null;
  budget: number | null;
  startDate: string | null;
  endDate: string | null;
}

export interface TrackedObject<S extends ObjectSummary = ObjectSummary> {
  // Null only when another company already tracks the Nexxen object.
  id: string | null;
  nexxenId: string | null;
  source: NexxenSourceType;
  syncStatus: SyncStatusType;
  lastError: string | null;
  readOnly: boolean;
  summary: S;
  nexxen: Record<string, unknown> | null;
}

export interface LineItemSummary extends ObjectSummary {
  cpm: number | null;
}

export type NexxenIO = TrackedObject;
export type NexxenLineItem = TrackedObject<LineItemSummary>;
export interface NexxenPackage extends TrackedObject {
  lineItems: NexxenLineItem[];
}
export interface NexxenIODetail {
  io: NexxenIO;
  packages: NexxenPackage[];
}

// Nexxen's current values in a flight's terms: market-local dates, total budget and the bid.
export interface NexxenFlightTerms {
  name: string | null;
  status: string | null;
  startDate: string | null;
  endDate: string | null;
  budget: number | null;
  cpm: number | null;
}

// A flight's line item from `GET /line-items/by-flight`.
export interface FlightLineItem {
  id: string;
  nexxenId: string | null;
  ioId: string;
  packageId: string;
  syncStatus: SyncStatusType;
  lastError: string | null;
  pausedReason: PausedReasonType | null;
  statusBeforePause: LineItemStatusType | null;
  // Null when Nexxen couldn't be read.
  nexxen?: NexxenFlightTerms | null;
}

export interface LevelResult {
  status: 'created' | 'failed' | 'skipped';
  id?: string;
  nexxenId?: string;
  error?: string;
}

export interface CreateLineItemBody {
  company: string;
  packageId: string;
  name: string;
  budget: number;
  startDate: string;
  endDate: string;
  cpm: number;
  geoCountries: string[];
  streamingFlightId: number;
  mediaFormat?: string;
  publisherDealId?: string;
}

// A deal a line item can target; `id` is what the create takes as `publisherDealId`.
export interface NexxenPublisherDeal {
  id: string;
  dealId: string | null;
  name: string | null;
  publisherName: string | null;
  endDate: string | null;
}

export interface LineItemChanges {
  name?: string;
  budget?: number;
  dailyCap?: number;
  startDate?: string;
  endDate?: string;
  cpm?: number;
}

export interface NexxenLocation {
  id: string;
  name: string;
}

export const NEXXEN_US_COUNTRY_ID = '2';

const NEXXEN_CONSOLE_HOST = 'https://platform.amobee.com/webapp/#';

export const NEXXEN_CONSOLE_BASE = `${NEXXEN_CONSOLE_HOST}/io-dashboard`;

export const nexxenIOUrl = (nexxenId: string): string =>
  `${NEXXEN_CONSOLE_BASE}/${nexxenId}/packages`;

export const nexxenLineItemUrl = (nexxenId: string): string =>
  `${NEXXEN_CONSOLE_HOST}/lineItem/${nexxenId}`;
